/**
 * storyRendersApi: a new-path Story item's rows, as Story Manager renders them for the reader.
 * Bare bodies, no envelope.
 *
 *   POST /api/v2/story/{s}/renders {item_ids, drill_through?}   -> 202 {render_id}
 *   GET  /api/v2/story/{s}/renders/{r}/items/{i}?offset&limit    -> {status, item?, rows?, masked_columns, offset, error?}
 *
 * A render runs without the request waiting: `status` is `pending` until it is done, so a read
 * is repeated until it says otherwise. Values above the reader's clearance come back `null`,
 * their columns named in `masked_columns`. The paths are local to this file, not in `API_ENDPOINTS`.
 */
import { apiService } from "../api";

const rendersPath = (storyId: string): string => `/api/v2/story/${storyId}/renders`;
const renderItemPath = (storyId: string, renderId: string, itemId: string): string =>
  `${rendersPath(storyId)}/${renderId}/items/${itemId}`;

/** The API's page ceiling (`limit` is 1-10000). */
export const RENDER_MAX_PAGE_SIZE = 10_000;

/** The rows behind a value column, no grouping: `row: {}` is the total row, so all of them. */
export interface StoryDrillThroughApi {
  kind: "drill_through";
  column: string;
  row: Record<string, never>;
}

export interface StoryRenderRequestApi {
  item_ids: string[];
  drill_through?: StoryDrillThroughApi;
}

export interface StoryRenderRequestedApi {
  render_id: string;
}

export type StoryItemRenderStatusApi = "pending" | "rendered" | "refused" | "failed";

/** One column of the answer. `name` is the key of each row. */
export interface StoryRenderColumnApi {
  name: string;
  kind?: string;
  type?: string;
  /** What a canvas may do with the column; `drill_through` lists the facts whose rows can be listed. */
  explore?: { drill_through?: unknown[] | null } | null;
}

export interface StoryRenderOutputApi {
  name?: string | null;
  /** Rows the output holds (what the pages add up to). */
  row_count?: number | null;
  /** Rows before a row limit cut them. */
  rows_before_limit?: number | null;
  columns: StoryRenderColumnApi[];
}

export interface StoryRenderProblemApi {
  code?: string;
  message?: string;
}

export interface StoryRenderAnswerApi {
  outputs?: StoryRenderOutputApi[];
  /** Set when the query was refused: there are no outputs then. */
  refusal?: { problems?: StoryRenderProblemApi[] } | null;
}

export interface StoryRenderedItemApi {
  item_id: string;
  /** Null for an item that holds no query (a note, a link). */
  answer?: StoryRenderAnswerApi | null;
}

export interface StoryRenderErrorApi {
  code?: string;
  message?: string;
  is_retryable?: boolean;
}

export interface StoryItemRenderApi {
  status: StoryItemRenderStatusApi;
  item?: StoryRenderedItemApi | null;
  /** One page of the answer's rows, by column name. */
  rows?: Record<string, unknown>[] | null;
  masked_columns?: string[];
  offset?: number;
  error?: StoryRenderErrorApi | null;
}

export interface StoryRenderPageParams {
  offset?: number;
  limit?: number;
}

/** `POST .../renders`: starts a fresh render of the items for this reader; with a column, the rows behind it (one item). */
export async function requestStoryRender(
  storyId: string,
  itemIds: string[],
  drillThroughColumn?: string,
): Promise<StoryRenderRequestedApi> {
  const body: StoryRenderRequestApi = { item_ids: itemIds };
  if (drillThroughColumn) body.drill_through = { kind: "drill_through", column: drillThroughColumn, row: {} };
  return apiService.post<StoryRenderRequestedApi>(rendersPath(storyId), body);
}

/** `GET .../renders/{r}/items/{i}`: one page of a render's rows, or `pending` while it runs. */
export async function readStoryItemRender(
  storyId: string,
  renderId: string,
  itemId: string,
  { offset = 0, limit = RENDER_MAX_PAGE_SIZE }: StoryRenderPageParams = {},
): Promise<StoryItemRenderApi> {
  return apiService.get<StoryItemRenderApi>(renderItemPath(storyId, renderId, itemId), { offset, limit });
}
